import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { Play, Pause, Edit2, Check, X, Loader2, ChevronDown, RefreshCw } from 'lucide-react';

export default function GoogleAdsCampaignsLab() {
  const { selectedBrand } = useAuth();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [error, setError] = useState(null);
  
  // State for tracking updates
  const [updatingId, setUpdatingId] = useState(null); // ID of entity being updated
  const [editBudget, setEditBudget] = useState(null); // { id: campaignId, value: number }
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null); // ID of campaign with open dropdown

  useEffect(() => {
    if (selectedBrand?.id) {
      fetchCampaigns();
    }
  }, [selectedBrand?.id]);

  const fetchCampaigns = async () => {
    if (!selectedBrand?.id) {
      // toast.error('Please select a brand first'); // Otomatik fetch'te sürekli hata vermesin
      return;
    }

    setLoading(true);
    setError(null);
    setCampaigns([]);

    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch campaigns');
      }

      if (data.success && Array.isArray(data.campaigns)) {
        setCampaigns(data.campaigns);
        toast.success(`Found ${data.campaigns.length} active/paused campaigns`);
      } else {
        throw new Error('Invalid response format');
      }

    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateEntity = async (entityType, entityId, field, value, campaignIdToUpdateInState) => {
    setUpdatingId(campaignIdToUpdateInState);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({
          entityType,
          entityId,
          field,
          value
        }),
        credentials: 'include'
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.details?.error || 'Update failed');
      }

      if (data.success) {
        toast.success('Updated successfully');
        // Optimistic update
        setCampaigns(prev => prev.map(c => {
          if (c.id === campaignIdToUpdateInState) {
            if (field === 'status') {
              return { ...c, status: value };
            } else if (field === 'amount_micros') {
              return { ...c, budget: parseInt(value) / 1000000 };
            }
          }
          return c;
        }));
      }

    } catch (err) {
      console.error('Update error:', err);
      toast.error(err.message);
    } finally {
      setUpdatingId(null);
      setEditBudget(null);
      setOpenStatusDropdown(null);
    }
  };

  const handleToggleStatus = (campaign) => {
    // Deprecated in favor of dropdown logic, but kept if needed
    const newStatus = campaign.status === 'ENABLED' ? 'PAUSED' : 'ENABLED';
    updateEntity('campaign', campaign.id, 'status', newStatus, campaign.id);
  };

  const handleSaveBudget = (campaign) => {
    if (!editBudget || editBudget.id !== campaign.id) return;
    
    // Convert to micros
    const micros = Math.round(parseFloat(editBudget.value) * 1000000);
    if (isNaN(micros) || micros <= 0) {
      toast.error('Invalid budget amount');
      return;
    }

    if (!campaign.budgetId) {
        toast.error('Budget ID not found for this campaign. Cannot update.');
        return;
    }

    updateEntity('budget', campaign.budgetId, 'amount_micros', micros, campaign.id);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google Ads Campaign Manager</h1>
          <p className="text-gray-500 mt-1">View live campaigns, toggle status, and adjust daily budgets.</p>
        </div>
        <button
          onClick={fetchCampaigns}
          disabled={loading}
          className={`p-2 rounded-lg transition-colors border ${
            loading 
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900'
          }`}
          title="Refresh Data"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && campaigns.length === 0 && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <p className="text-gray-500">No campaigns found.</p>
        </div>
      )}

      {campaigns.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Budget</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost (All Time)</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impr. / Clicks</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{campaign.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap relative">
                      {/* Status Dropdown Trigger */}
                      <button
                        onClick={() => setOpenStatusDropdown(openStatusDropdown === campaign.id ? null : campaign.id)}
                        disabled={updatingId === campaign.id}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none ${updatingId === campaign.id ? 'opacity-50 cursor-wait' : ''}`}
                      >
                         {updatingId === campaign.id ? (
                           <Loader2 className="w-3 h-3 animate-spin text-gray-500" />
                         ) : (
                           <div className={`w-2.5 h-2.5 rounded-full ${campaign.status === 'ENABLED' ? 'bg-green-500 shadow-[0_0_0_2px_rgba(34,197,94,0.2)]' : 'bg-gray-400'}`} />
                         )}
                         <span className={`text-sm font-medium ${campaign.status === 'ENABLED' ? 'text-gray-900' : 'text-gray-500'}`}>
                           {campaign.status === 'ENABLED' ? 'Enabled' : 'Paused'}
                         </span>
                         <ChevronDown className="w-3 h-3 text-gray-400" />
                      </button>

                      {/* Dropdown Menu */}
                      {openStatusDropdown === campaign.id && (
                        <>
                          <div className="fixed inset-0 z-10 cursor-default" onClick={() => setOpenStatusDropdown(null)} />
                          <div className="absolute top-full left-0 mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-20 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left">
                             <button
                                onClick={() => updateEntity('campaign', campaign.id, 'status', 'ENABLED', campaign.id)}
                                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                  ${campaign.status === 'ENABLED' ? 'text-green-600 bg-green-50/50' : 'text-gray-700'}
                                `}
                             >
                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                <span>Enable</span>
                                {campaign.status === 'ENABLED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                             </button>
                             <button
                                onClick={() => updateEntity('campaign', campaign.id, 'status', 'PAUSED', campaign.id)}
                                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                  ${campaign.status === 'PAUSED' ? 'text-gray-900 bg-gray-50' : 'text-gray-700'}
                                `}
                             >
                                <div className="w-2 h-2 rounded-full bg-gray-400" />
                                <span>Pause</span>
                                {campaign.status === 'PAUSED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                             </button>
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">{campaign.type}</td>
                    
                    {/* Budget Cell */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {editBudget && editBudget.id === campaign.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <input
                            type="number"
                            value={editBudget.value}
                            onChange={(e) => setEditBudget({ ...editBudget, value: e.target.value })}
                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                            autoFocus
                          />
                          <button 
                            onClick={() => handleSaveBudget(campaign)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => setEditBudget(null)}
                            className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2 group">
                          <span className="text-sm text-gray-900 font-medium">
                            {campaign.budget ? campaign.budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                          </span>
                          <button 
                             onClick={() => setEditBudget({ id: campaign.id, value: campaign.budget })}
                             disabled={updatingId === campaign.id}
                             className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 transition-opacity"
                          >
                             <Edit2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {campaign.cost ? campaign.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                      <div>{campaign.impressions?.toLocaleString()} <span className="text-xs text-gray-400">Imp</span></div>
                      <div>{campaign.clicks?.toLocaleString()} <span className="text-xs text-gray-400">Clicks</span></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
